import React from 'react';
import {
  InfoCircleOutlined,
  MailOutlined,
  MessageOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Popover, Space, Typography } from 'antd';
import dayjs from 'dayjs';

import { PaperPlaneOutlined } from '../MabelIcons';

type CampaignChannel = 'Email' | 'Letter' | 'SMS';

type ApprovedCampaignComponent = {
  campaignName: string;
  channel: CampaignChannel;
  campaignIsArchived: boolean;
  componentIsArchived: boolean;
  isCancelled: boolean;
  practiceName?: string;
  sendDatetime: string;
};

export type PracticeCampaignApprovals = {
  email_component_approvals: Array<{
    is_cancelled: boolean;
    campaign_email_component: {
      send_datetime: string;
      is_archived: boolean;
      campaign_template: {
        campaign_name: string;
        is_archived: boolean;
      };
    };
  }>;
  letter_component_approvals: Array<{
    is_cancelled: boolean;
    campaign_letter_component: {
      send_datetime: string;
      is_archived: boolean;
      campaign_template: {
        campaign_name: string;
        is_archived: boolean;
      };
    };
  }>;
  sms_component_approvals: Array<{
    is_cancelled: boolean;
    campaign_sms_component: {
      send_datetime: string;
      is_archived: boolean;
      campaign_template: {
        campaign_name: string;
        is_archived: boolean;
      };
    };
  }>;
};

export type NetworkCampaignApprovals = {
  practices: Array<
    PracticeCampaignApprovals & {
      practice_name: string;
    }
  >;
};

const partitionCampaignComponentsByTiming = (
  components: ApprovedCampaignComponent[]
) => {
  const groups: {
    past: ApprovedCampaignComponent[];
    upcoming: ApprovedCampaignComponent[];
  } = { past: [], upcoming: [] };
  const now = dayjs();

  components.forEach((component) => {
    if (
      component.isCancelled ||
      component.componentIsArchived ||
      component.campaignIsArchived
    )
      return;

    const sendDatetime = dayjs(component.sendDatetime);
    if (!sendDatetime.isValid()) return;

    groups[sendDatetime.isAfter(now) ? 'upcoming' : 'past'].push(component);
  });

  groups.upcoming.sort(
    (a, b) => dayjs(a.sendDatetime).valueOf() - dayjs(b.sendDatetime).valueOf()
  );
  groups.past.sort(
    (a, b) => dayjs(b.sendDatetime).valueOf() - dayjs(a.sendDatetime).valueOf()
  );

  return groups;
};

const campaignComponentsForPractice = (
  practice: PracticeCampaignApprovals,
  practiceName?: string
) => [
  ...practice.email_component_approvals.map((approval) => ({
    campaignName:
      approval.campaign_email_component.campaign_template.campaign_name,
    channel: 'Email' as const,
    campaignIsArchived:
      approval.campaign_email_component.campaign_template.is_archived,
    componentIsArchived: approval.campaign_email_component.is_archived,
    isCancelled: approval.is_cancelled,
    practiceName,
    sendDatetime: approval.campaign_email_component.send_datetime,
  })),
  ...practice.letter_component_approvals.map((approval) => ({
    campaignName:
      approval.campaign_letter_component.campaign_template.campaign_name,
    channel: 'Letter' as const,
    campaignIsArchived:
      approval.campaign_letter_component.campaign_template.is_archived,
    componentIsArchived: approval.campaign_letter_component.is_archived,
    isCancelled: approval.is_cancelled,
    practiceName,
    sendDatetime: approval.campaign_letter_component.send_datetime,
  })),
  ...practice.sms_component_approvals.map((approval) => ({
    campaignName:
      approval.campaign_sms_component.campaign_template.campaign_name,
    channel: 'SMS' as const,
    campaignIsArchived:
      approval.campaign_sms_component.campaign_template.is_archived,
    componentIsArchived: approval.campaign_sms_component.is_archived,
    isCancelled: approval.is_cancelled,
    practiceName,
    sendDatetime: approval.campaign_sms_component.send_datetime,
  })),
];

export const getCampaignComponents = (
  practice: PracticeCampaignApprovals,
  practiceName?: string
) =>
  partitionCampaignComponentsByTiming(
    campaignComponentsForPractice(practice, practiceName)
  );

export const getNetworkCampaignComponents = (
  network: NetworkCampaignApprovals
) =>
  partitionCampaignComponentsByTiming(
    network.practices.flatMap((practice) =>
      campaignComponentsForPractice(practice, practice.practice_name)
    )
  );

const CampaignChannelIcon: React.FC<{ channel: CampaignChannel }> = ({
  channel,
}) => {
  if (channel === 'Letter') return <PaperPlaneOutlined />;

  return (
    <span aria-label={channel} role="img">
      {channel === 'Email' ? <MailOutlined /> : <MessageOutlined />}
    </span>
  );
};

const CampaignComponentList: React.FC<{
  components: ApprovedCampaignComponent[];
  emptyText: string;
  title: string;
  warning?: boolean;
}> = ({ components, emptyText, title, warning = false }) => (
  <Space direction="vertical" size={4} style={{ display: 'flex' }}>
    <Typography.Text strong type={warning ? 'warning' : undefined}>
      {title} ({components.length})
    </Typography.Text>
    {components.length > 0 ? (
      components.map((component, index) => (
        <Space
          align="start"
          key={`${component.channel}-${component.sendDatetime}-${index}`}
          size={6}
        >
          <CampaignChannelIcon channel={component.channel} />
          <div>
            <Typography.Text style={{ display: 'block' }}>
              {component.campaignName}
            </Typography.Text>
            <Typography.Text type="secondary">
              {component.practiceName ? `${component.practiceName} · ` : null}
              {dayjs(component.sendDatetime).format('lll')}
            </Typography.Text>
          </div>
        </Space>
      ))
    ) : (
      <Typography.Text type="secondary">{emptyText}</Typography.Text>
    )}
  </Space>
);

const CampaignsPopover: React.FC<{
  components: ReturnType<typeof partitionCampaignComponentsByTiming>;
  includePast?: boolean;
  title: string;
  warningText: string;
}> = ({ components, includePast = true, title, warningText }) => {
  const hasUpcomingCampaigns = components.upcoming.length > 0;
  const popoverContent = (
    <div style={{ maxHeight: 360, overflowY: 'auto', width: 360 }}>
      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
        <CampaignComponentList
          components={components.upcoming}
          emptyText="No upcoming campaigns"
          title="Upcoming"
          warning={hasUpcomingCampaigns}
        />
        {includePast ? (
          <CampaignComponentList
            components={components.past}
            emptyText="No past campaigns"
            title="Past"
          />
        ) : null}
        {hasUpcomingCampaigns ? (
          <Typography.Text type="warning">{warningText}</Typography.Text>
        ) : null}
      </Space>
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      placement="left"
      title={title}
      trigger="hover"
    >
      <span
        aria-label={`${components.upcoming.length} upcoming ${
          components.upcoming.length === 1 ? 'campaign' : 'campaigns'
        }${
          includePast
            ? ` and ${components.past.length} past ${
                components.past.length === 1 ? 'campaign' : 'campaigns'
              }`
            : ''
        }`}
        role="img"
        style={{
          color: hasUpcomingCampaigns ? '#d48806' : '#bfbfbf',
          cursor: 'help',
          fontSize: 18,
        }}
      >
        {hasUpcomingCampaigns ? <WarningOutlined /> : <InfoCircleOutlined />}
      </span>
    </Popover>
  );
};

const PracticeCampaignsPopover: React.FC<{
  practice: PracticeCampaignApprovals;
}> = ({ practice }) => (
  <CampaignsPopover
    components={getCampaignComponents(practice)}
    title="Approved campaigns"
    warningText="Practice changes may affect upcoming campaigns."
  />
);

export const NetworkCampaignsPopover: React.FC<{
  network: NetworkCampaignApprovals;
}> = ({ network }) => (
  <CampaignsPopover
    components={getNetworkCampaignComponents(network)}
    includePast={false}
    title="Upcoming approved campaigns"
    warningText="Network changes may affect upcoming campaigns."
  />
);

export default PracticeCampaignsPopover;
